import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styles from './TimesheetsPage.module.css'
import { useAuth } from '../contexts/AuthContext'
import { hasPower, useAccess } from '../lib/access'
import { apiJson } from '../lib/api'
import { useHelpPref } from '../lib/preferences'
import { PAY_TABS, PairTabs } from '../components/PairTabs'
import { PageHeader, PageBody, headerStyles } from '../components/PageHeader'
import { usePickerDismiss } from '../components/pickerDismiss'
import { SkeletonRows } from '../components/pending'
import { AsAt } from '../components/AsAt'
import { useRefetch } from '../lib/connection'
import { useLive } from '../lib/live'
import { ShiftEditor } from './timesheets/ShiftEditor'
import { BulkModal, BULK_BUTTONS, type BulkOp } from './timesheets/BulkModal'
import { AddShiftModal } from './timesheets/AddShiftModal'
import { BreakScheduleModal } from './timesheets/BreakScheduleModal'
import { TimesheetSettingsModal, type TimesheetSettings } from './timesheets/TimesheetSettingsModal'
import { addDaysLocal, dateLabel, fmtDur, fmtHm, fmtStamp, isoLocal, MONS, parseIsoLocal, shortDateLabel, todayLocal, wallMinutes } from './timesheets/time'
import { DEFAULT_TEAM_COLOUR, isAdjustment, isProvisional, type Person, type Shift, type Team } from './timesheets/types'
import { openableRow } from '../lib/openableRow'
import { datesLabel, readKept, useKeptDates } from '../lib/keptDates'
import { StillLooking } from '../components/StillLooking'

// Timesheets (CONTROL V4 tsRows / buildRow, handoff §7f): one work day, one row
// per worker. Every figure on a row is the API's (shift-math); this screen only
// filters and formats. Payable is net payable time — recorded session time
// less any unpaid break not already covered by a gap — never paid-break minutes.

type DayTone = 'live' | 'closed' | 'locked' | 'future'

const TONE: Record<DayTone, { bg: string; line: string; ink: string; dot: string; name: string }> = {
  live: { bg: '#fbeecd', line: '#eedcae', ink: '#7a5410', dot: '#c78f2c', name: 'Live' },
  closed: { bg: '#f1f4e8', line: '#d3ddba', ink: '#3f4a2c', dot: '#7a8a5e', name: 'Closed' },
  locked: { bg: '#f6f0e6', line: '#e2dacb', ink: '#5c5449', dot: '#857c6d', name: 'Locked' },
  future: { bg: '#f6f0e6', line: '#e2dacb', ink: '#857c6d', dot: '#c1b7a4', name: 'Not yet' },
}

const LINE: Record<DayTone, string> = {
  live: 'Devices are still reporting and the day may still change. An adjustment made now is overtaken when a real clock-off arrives, so the correction is lost without warning.',
  closed: 'The operational day is complete. Events that arrive now are held as late data for approval, not written silently over what payroll already saw.',
  locked: 'Payroll is finalised. Changes need a deliberate administrative unlock.',
  future: 'This day has not happened, so there is nothing to reconcile yet.',
}

// On the Excluded tab nothing reaches wages, so the live-day warning would be untrue.
const EXCL_LINE: Record<DayTone, string> = {
  live: 'These shifts are excluded from wages, so nothing here is paid and an adjustment cannot affect a wage figure. Devices are still reporting, so a shift can still change or be re-included.',
  closed: 'These shifts are excluded from wages. The operational day is complete; re-including one puts it back in front of payroll as a change they can see.',
  locked: 'These shifts are excluded from wages and payroll is finalised. Re-including one needs the same deliberate unlock as any other change.',
  future: 'This day has not happened, so there is nothing excluded yet.',
}

const HELP: [string, React.ReactNode][] = [
  [
    'One day at a time',
    'This is the reconciliation screen for a single work day. Rows come from clock-ons and clock-offs in Dataphyll Crew, one row per worker, holding every session they worked that day. Get the day right here and wages follow on their own.',
  ],
  [
    'Live, closed or locked',
    'The chip beside the date says which. On a live day devices are still reporting, so a correction can be overtaken by a real clock-off arriving later. A closed day holds late events for approval instead of overwriting. A locked day is one CONTROL no longer changes, and it must be unlocked before anything changes. Locking is not proof of what was paid.',
  ],
  [
    'Included and excluded',
    <>
      Excluding a row takes those hours out of the day being reconciled, so it leaves the list rather than sitting greyed out. Nothing is deleted and the row stays on the Excluded tab with its history. <strong>Need attention</strong> pulls up the rows with a missing clock-off, overlapping sessions or something else that would pay wrongly.
    </>,
  ],
  [
    'Breaks are applied, not typed',
    'The break schedule grants paid and unpaid breaks from the hours worked, and the version in force is the one dated on or before the day you are looking at. Changing it forward creates a new version. Changing it retrospectively rewrites days that may already have been paid.',
  ],
  [
    'Editing, and what it costs',
    'Open a row to fix one person, or tick several for the bulk actions: clock everyone off at a time, adjust minutes, set breaks, split sessions by team. Every change is recorded with who made it and why, and it marks that worker and day for a wage recalculation.',
  ],
]

const asArray = <T,>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : [])

// Earliest start and latest finish over the complete sessions, in minutes from
// the start of the work date, so a finish after midnight reads as ≥ 1440.
function spanOf(sh: Shift): { s: number; e: number } | null {
  const iv = sh.metrics?.intervals
  if (iv && iv.length) {
    let s = Infinity
    let e = -Infinity
    for (const x of iv) {
      s = Math.min(s, wallMinutes(x.start, sh.work_date))
      e = Math.max(e, wallMinutes(x.end, sh.work_date))
    }
    return { s, e }
  }
  return null
}

// With no complete session the span is unknown, but the clock-on is not: the earliest session start, as the session
// list prints it, for "07:20 – —".
function clockOnOf(sh: Shift): string | null {
  const first = [...(sh.sessions || [])].filter((x) => x.start_time).sort((a, b) => `${a.start_date} ${a.start_time}`.localeCompare(`${b.start_date} ${b.start_time}`))[0]
  return first ? first.start_time.slice(0, 5) : null
}

const hasComplete = (sh: Shift) => (sh.sessions || []).some((s) => s.duration_minutes != null) || !!sh.metrics?.intervals?.length

const DAY_KEY = 'timesheets.day'
const isDay = (d: unknown): d is string => typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d)

export function TimesheetsPage() {
  const { user } = useAuth()
  const today = todayLocal()
  // A report's drill-through opens a day for one team (?date, ?team; §7l.4).
  // Read once, on arrival; the screen owns both from then on.
  const [params] = useState(() => new URLSearchParams(window.location.search))
  // Otherwise the day last looked at in this tab today, so going to Wages and
  // back keeps it (lib/keptDates.ts); a new tab or a new day starts on today.
  const [date, setDate] = useState(() => {
    const d = params.get('date')
    return isDay(d) ? d : readKept(DAY_KEY, user?.uid, today, isDay) || today
  })
  const keptDay = useKeptDates(DAY_KEY, user?.uid, today, date)
  const [shifts, setShifts] = useState<Shift[]>([])
  const [serverState, setServerState] = useState<'LIVE' | 'CLOSED' | 'LOCKED'>('LIVE')
  const [teams, setTeams] = useState<Team[]>([])
  const [people, setPeople] = useState<Person[]>([])
  // The date whose shifts are shown; used to track loading for skeleton rows (h 3994-4002)
  const [shown, setShown] = useState<string | null>(null)
  const [asAt, setAsAt] = useState<Date | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [flash, setFlash] = useState('')
  const access = useAccess()
  // Day close, lock and unlock (h 2526-2527; D1.6): the actions sit beside the day chip.
  const [dayAct, setDayAct] = useState<'close' | 'lock' | 'unlock' | null>(null)
  const [unlockWhy, setUnlockWhy] = useState('')
  const [dayBusy, setDayBusy] = useState(false)
  // The confirmation is an inline panel, not a modal: focus goes into it when it opens (so a keyboard or
  // screen-reader user lands on it), Escape or Cancel closes it, and focus goes back to the button that opened it.
  const dayPanel = useRef<HTMLDivElement>(null)
  const dayTrigger = useRef<HTMLElement | null>(null)
  const openDayAct = (kind: 'close' | 'lock' | 'unlock', e: React.MouseEvent<HTMLElement>) => {
    dayTrigger.current = e.currentTarget
    setDayAct(kind)
  }
  const cancelDayAct = () => {
    setDayAct(null)
    if (dayTrigger.current?.isConnected) dayTrigger.current.focus()
  }
  useEffect(() => {
    if (dayAct) dayPanel.current?.querySelector<HTMLElement>('[data-autofocus]')?.focus()
  }, [dayAct])
  const [dayMsg, setDayMsg] = useState('')

  const [tab, setTab] = useState<'included' | 'excluded'>('included')
  const [teamFilter, setTeamFilter] = useState<string | null>(() => params.get('team') || null)
  const [teamOpen, setTeamOpen] = useState(false)
  // The team list is a layer (h 425-458): Escape closes it, and so does a
  // press outside it and its trigger.
  const teamWrap = usePickerDismiss(teamOpen, () => setTeamOpen(false))
  const [calOpen, setCalOpen] = useState(false)
  const [calMonth, setCalMonth] = useState(() => date.slice(0, 7))
  // The day stepper holds the calendar and its trigger (h 440-447).
  const calWrap = usePickerDismiss(calOpen, () => setCalOpen(false))
  const [query, setQuery] = useState('')
  const [attn, setAttn] = useState(false)
  const [picked, setPicked] = useState<string[]>([])
  const [help, setHelp] = useHelpPref(user, 'sheets')

  const [openId, setOpenId] = useState<string | null>(null)
  const [bulk, setBulk] = useState<BulkOp | null>(null)
  const [adding, setAdding] = useState(false)
  const [schedOpen, setSchedOpen] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [tsSettings, setTsSettings] = useState<TimesheetSettings | null>(null)

  // The signed-in user is read through a ref so a new user object for the same
  // account (a token refresh) does not re-read the day.
  const userRef = useRef(user)
  userRef.current = user
  const uid = user?.uid
  const seq = useRef(0)
  // The date the shifts shown were loaded for: a refetch of it that fails keeps them up and says so; another date's failure shows none of the old.
  const shiftsKey = useRef<string | null>(null)
  const load = useCallback(async () => {
    const user = userRef.current
    if (!user) return
    const n = ++seq.current
    setError(null)
    try {
      const [list, day, tm, ppl, tss] = await Promise.all([
        apiJson<Shift[]>(user, `/api/timesheets?work_date=${date}`),
        apiJson<{ state: 'LIVE' | 'CLOSED' | 'LOCKED' }>(user, `/api/timesheets/day/${date}`).catch(() => null),
        apiJson<Team[]>(user, '/api/teams').catch(() => []),
        apiJson<Person[]>(user, '/api/personnel').catch(() => []),
        apiJson<TimesheetSettings>(user, '/api/timesheets/settings').catch(() => null),
      ])
      if (n !== seq.current) return
      shiftsKey.current = date
      setShifts(asArray<Shift>(list))
      setTsSettings(tss && typeof tss.edit_current_day === 'boolean' ? tss : null)
      if (day && day.state) setServerState(day.state)
      setTeams(asArray<Team>(tm))
      setPeople(asArray<Person>(ppl).filter((p) => !p.archived))
      setAsAt(new Date())
    } catch (e: any) {
      if (n !== seq.current) return
      if (shiftsKey.current !== date) {
        shiftsKey.current = null
        setShifts([])
      }
      setError(e?.message || 'Could not load timesheets')
    } finally {
      if (n === seq.current) setShown(date)
    }
  }, [uid, date])

  useEffect(() => {
    load()
  }, [load])
  // h 4167-4171: it fetches again on window focus and on reconnect, and today
  // is pushed: a shift or a scan (the worked intervals) written on it fetches
  // the day again.
  useRefetch(load)
  const live = useLive(user, ['shifts', 'cycles'], date === today ? (d) => d === date : null, load)

  const runDayAct = async () => {
    if (!dayAct || !user) return
    setDayBusy(true)
    setDayMsg('')
    try {
      const body = dayAct === 'unlock' ? { work_date: date, reason: unlockWhy.trim() || undefined } : { work_date: date }
      const url = dayAct === 'close' ? '/api/wages/close-day' : dayAct === 'lock' ? '/api/wages/lock-day' : '/api/wages/unlock-day'
      const r = await apiJson<{ success?: boolean; message?: string; recalculation_queued?: number }>(user, url, { method: 'POST', body })
      if (r && r.success === false) setDayMsg(r.message || 'The day was not changed.')
      else if (dayAct === 'close') setDayMsg('Day closed.')
      else if (dayAct === 'lock') setDayMsg('Day locked.')
      else setDayMsg(r?.recalculation_queued === 0 ? 'Day unlocked; there were no worker-days to recalculate.' : 'Day unlocked; recalculation queued.')
      setDayAct(null)
      setUnlockWhy('')
      await load()
    } catch (e: any) {
      setDayMsg(e?.message || 'The day was not changed.')
    } finally {
      setDayBusy(false)
    }
  }

  const goDate = (iso: string) => {
    setDate(iso)
    setCalMonth(iso.slice(0, 7))
    setOpenId(null)
    setPicked([])
    setDayAct(null)
    setDayMsg('')
  }

  const tone: DayTone = date > today ? 'future' : (serverState.toLowerCase() as DayTone)
  const excl = tab === 'excluded'

  const colourOf = useMemo(() => {
    const m = new Map(teams.map((t) => [t.id, t.colour || DEFAULT_TEAM_COLOUR]))
    return (id: string) => m.get(id) || DEFAULT_TEAM_COLOUR
  }, [teams])

  // Team options: active non-induction teams, plus any team on the day's sessions.
  const teamOptions = useMemo(() => {
    const onDay = new Set(shifts.flatMap((s) => s.team_ids || []))
    const out = teams.filter((t) => !t.is_induction && (t.active !== false || onDay.has(t.id)))
    for (const s of shifts)
      (s.team_ids || []).forEach((id, i) => {
        if (!out.some((t) => t.id === id)) out.push({ id, name: s.team_names?.[i] || 'Team' })
      })
    return out
  }, [teams, shifts])
  const pickableTeams = teams.filter((t) => !t.is_induction && t.active !== false)

  const onTab = shifts.filter((s) => (excl ? s.exclude_from_wages : !s.exclude_from_wages))
  const attnCount = onTab.filter(isProvisional).length
  const openCount = onTab.filter((s) => s.incomplete || (s.metrics?.open_session_ids?.length ?? 0) > 0).length
  const q = query.trim().toLowerCase()
  const rows = onTab.filter((s) => {
    if (teamFilter && !(s.team_ids || []).includes(teamFilter)) return false
    if (q && !(s.worker_name || '').toLowerCase().includes(q)) return false
    if (attn && !isProvisional(s)) return false
    return true
  })

  const allOn = picked.length > 0 && picked.length === rows.length
  const toggle = (id: string) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : p.concat(id)))

  const dayFull =
    (excl ? EXCL_LINE[tone] : LINE[tone]) +
    (!excl && tone === 'live' && openCount ? ' ' + (openCount === 1 ? '1 worker has no clock-off yet.' : openCount + ' workers have no clock-off yet.') : '')

  const emptyLabel = attn
    ? 'No worker on this date needs attention.'
    : query
      ? `No worker matches “${query}” on this date.`
      : excl
        ? 'No shift on this date is excluded from wages.'
        : 'No shifts recorded on this date.'

  // A write replaces the flash: the server's message when it has one, otherwise
  // none, so an earlier flash never reads as the outcome of this write (V4
  // clears tsFlash on a plain save).
  const written = (msg?: string) => {
    setFlash(msg || '')
    load()
  }

  const loading = shown !== date
  // A failed read of this date with nothing of it to show: the error says so; "no shifts" would be untrue.
  const kept = !!error && shiftsKey.current === date
  const teamLabel = teamFilter ? teamOptions.find((t) => t.id === teamFilter)?.name || 'Team' : 'All teams'

  const headerActions = (
    <>
      <button className={headerStyles.btn} onClick={() => setSchedOpen(true)}>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M17 8h1a4 4 0 1 1 0 8h-1" />
          <path d="M3 8h14v9a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4Z" />
          <path d="M6 2v2M10 2v2M14 2v2" />
        </svg>
        Break schedule
      </button>
      <button className={headerStyles.btn} onClick={() => setSettingsOpen(true)}>
        Timesheet settings
      </button>
      <button className={`${headerStyles.btn} ${headerStyles.btnPrimary}`} onClick={() => setAdding(true)}>
        Add worker shift
      </button>
      <button
        className={headerStyles.btnHelp}
        aria-expanded={help}
        aria-controls="sheets-how-it-works"
        onClick={() => {
          setHelp(!help)
        }}
      >
        {help ? 'Hide help' : 'How timesheets work'}
      </button>
    </>
  )

  return (
    <div className={styles.page}>
      <StillLooking at={keptDay.at} onDefaults={date === today} label={datesLabel(date, date, today)} onContinue={keptDay.confirm} onDefault={() => setDate(today)} />
      <PageHeader
        title="Timesheets"
        actions={headerActions}
        tabs={<PairTabs label="Pay" tabs={PAY_TABS} current="Timesheets" />}
      />

      <PageBody>
          {help && (
          <section className={styles.helpGrid} id="sheets-how-it-works" role="region" aria-labelledby="sheets-how-it-works-title">
            <h2 id="sheets-how-it-works-title" style={{ display: 'none' }}>How timesheets work</h2>
            <button type="button" className={styles.helpX} aria-label="Close How timesheets work" onClick={() => setHelp(false)}>
              ×
            </button>
            {HELP.map(([h, body]) => (
              <div key={h} className={styles.helpCard}>
                <span>{h}</span>
                <p>{body}</p>
              </div>
            ))}
          </section>
        )}

      <div className={styles.toolbar}>
        <div className={styles.datePill} ref={calWrap}>
          <button className={styles.dateArrow} aria-label="Previous day" onClick={() => goDate(addDaysLocal(date, -1))}>
            ‹
          </button>
          <button className={styles.dateLabel} onClick={() => setCalOpen((o) => !o)} aria-expanded={calOpen}>
            {dateLabel(date)}
          </button>
          <button className={styles.dateArrow} aria-label="Next day" onClick={() => goDate(addDaysLocal(date, 1))}>
            ›
          </button>
          {calOpen && (
            <Calendar
              month={calMonth}
              picked={date}
              today={today}
              onMonth={setCalMonth}
              onPick={(iso) => {
                goDate(iso)
                setCalOpen(false)
              }}
            />
          )}
        </div>
        {/* The day state belongs to the selected date, so it sits beside the
            stepper rather than in the header (h 1853-1856). */}
        <span
          className={styles.dayChip}
          style={{ background: TONE[tone].bg, borderColor: TONE[tone].line, color: TONE[tone].ink }}
          title={dayFull}
          data-testid="day-chip"
        >
          <span className={styles.dayDot} style={{ background: TONE[tone].dot }} />
          {(excl ? 'Excluded · ' : '') + TONE[tone].name}
          {!excl && tone === 'live' && openCount > 0 && <span className={styles.dayOpen}>{openCount} open</span>}
        </span>
        {tone === 'live' && hasPower(access, 'day_close') && (
          <button className={styles.teamBtn} data-testid="day-close" onClick={(e) => openDayAct('close', e)} disabled={dayBusy}>
            Close day
          </button>
        )}
        {tone === 'closed' && hasPower(access, 'day_lock') && (
          <button className={styles.teamBtn} data-testid="day-lock" onClick={(e) => openDayAct('lock', e)} disabled={dayBusy}>
            Lock day
          </button>
        )}
        {tone === 'locked' && hasPower(access, 'day_lock') && (
          <button className={styles.teamBtn} data-testid="day-unlock" onClick={(e) => openDayAct('unlock', e)} disabled={dayBusy}>
            Unlock day
          </button>
        )}
        {!loading && <AsAt at={asAt} live={live === 'live'} />}

        <div className={styles.teamPick} ref={teamWrap}>
          <button className={styles.teamBtn} onClick={() => setTeamOpen((o) => !o)} aria-haspopup="listbox" aria-expanded={teamOpen}>
            <span className={teamFilter ? styles.teamDot : `${styles.teamDot} ${styles.teamDotAll}`} style={teamFilter ? { background: colourOf(teamFilter) } : undefined} />
            <span className={styles.teamBtnLabel}>{teamLabel}</span>
          </button>
          {teamOpen && (
            <div className={styles.teamMenu} role="listbox" aria-label="Team">
              {[{ id: null as string | null, name: 'All teams' }, ...teamOptions].map((t) => {
                const on = teamFilter === t.id
                return (
                  <button
                    key={t.id || 'all'}
                    role="option"
                    aria-selected={on}
                    className={on ? `${styles.teamOpt} ${styles.teamOptPicked}` : styles.teamOpt}
                    onClick={() => {
                      setTeamFilter(t.id)
                      setTeamOpen(false)
                      setPicked([])
                    }}
                  >
                    <span className={t.id ? styles.teamDot : `${styles.teamDot} ${styles.teamDotAll}`} style={t.id ? { background: colourOf(t.id) } : undefined} />
                    {t.name}
                    <span className={styles.teamTick}>{on ? '✓' : ''}</span>
                  </button>
                )
              })}
            </div>
          )}
        </div>

        <input className={styles.search} placeholder="Search workers" aria-label="Search workers" value={query} onChange={(e) => setQuery(e.target.value)} />

        {(attnCount > 0 || attn) && (
          <button className={attn ? `${styles.attnChip} ${styles.attnOn}` : styles.attnChip} aria-pressed={attn} onClick={() => setAttn((a) => !a)}>
            {attnCount} need attention
          </button>
        )}
      </div>

      {/* h 2900: the business corrects a day once it is over. Said up front so
          an edit is not refused only after it has been typed. */}
      {tsSettings && !tsSettings.edit_current_day && date === tsSettings.today && (
        <div className={styles.notice} data-testid="today-not-editable" style={{ marginBottom: 12 }}>
          Today’s timesheets cannot be edited: this business corrects a day once it is over. Comments can still be added, and Clock all off still ends the day.
        </div>
      )}
      {dayAct && (
        <div className={styles.notice} data-testid="day-confirm" ref={dayPanel} onKeyDown={(e) => { if (e.key === 'Escape' && !dayBusy) cancelDayAct() }} role="alertdialog" aria-label={dayAct === 'close' ? 'Close this day' : dayAct === 'lock' ? 'Lock this day' : 'Unlock this day'} style={{ marginBottom: 12 }}>
          {dayAct === 'close' ? (
            <p style={{ margin: '0 0 8px' }}>
              Close {dateLabel(date)}? CONTROL runs the final pass over the day, and events that arrive afterwards are held as late data for approval. A closed day does not reopen by itself.
            </p>
          ) : dayAct === 'lock' ? (
            <p style={{ margin: '0 0 8px' }}>
              Lock {dateLabel(date)}? Payroll is finalised for the day and it is locked against ordinary edits; changing it again needs an administrator to unlock it, and unlocking queues a recalculation. The lock is recorded against you.
            </p>
          ) : (
            <>
              <p style={{ margin: '0 0 8px' }}>
                Unlock {dateLabel(date)}? It goes back to Closed and can be changed again. Unlocking queues a recalculation of this day’s worker-days under the rules and settings in force now, so wage figures for the day may change. The recalculation runs afterwards; it has not finished when you confirm. The unlock is recorded against you.
              </p>
              <input
                data-autofocus
                aria-label="Reason for unlocking"
                placeholder="Reason (optional)"
                value={unlockWhy}
                onChange={(e) => setUnlockWhy(e.target.value)}
                style={{ marginBottom: 8, width: '100%', maxWidth: 360 }}
              />
            </>
          )}
          <button className={styles.teamBtn} data-testid="day-confirm-go" onClick={runDayAct} disabled={dayBusy}>
            {dayAct === 'close' ? 'Close day' : dayAct === 'lock' ? 'Lock day' : 'Unlock day'}
          </button>{' '}
          <button className={styles.teamBtn} data-autofocus={dayAct !== 'unlock' ? true : undefined} onClick={cancelDayAct} disabled={dayBusy}>
            Cancel
          </button>
        </div>
      )}
      {dayMsg && (
        <div className={styles.flash} role="status" data-testid="day-msg">
          <span className={styles.flashNote}>{dayMsg}</span>
          <button className={styles.flashX} aria-label="Dismiss" onClick={() => setDayMsg('')}>
            ×
          </button>
        </div>
      )}
      {flash && (
        <div className={styles.flash} role="status">
          <span className={styles.flashNote}>{flash}</span>
          <button className={styles.flashX} aria-label="Dismiss" onClick={() => setFlash('')}>
            ×
          </button>
        </div>
      )}
      {error && (
        <div className={styles.errorBar} role="alert">
          <span>{kept ? `Timesheets could not be refreshed: ${error}. What is shown may be out of date.` : error}</span>
          <button className={styles.flashX} onClick={() => load()}>
            Retry
          </button>
        </div>
      )}

      {picked.length > 0 && (
        <div className={styles.selBar}>
          <strong>
            {picked.length} worker{picked.length === 1 ? '' : 's'} selected
          </strong>
          {BULK_BUTTONS.map((b) => (
            <button key={b.op} className={styles.selBtn} onClick={() => setBulk(b.op)}>
              {b.label}
            </button>
          ))}
          <button className={styles.selClear} onClick={() => setPicked([])}>
            Clear
          </button>
        </div>
      )}

      <div className={styles.card}>
        <div className={styles.cardHead}>
          <div className={styles.tabTrack}>
            {(
              [
                ['included', 'Included', shifts.filter((s) => !s.exclude_from_wages).length],
                ['excluded', 'Excluded', shifts.filter((s) => s.exclude_from_wages).length],
              ] as const
            ).map(([k, label, count]) => (
              <button
                key={k}
                className={tab === k ? `${styles.tab} ${styles.active}` : styles.tab}
                onClick={() => {
                  setTab(k)
                  setOpenId(null)
                  setPicked([])
                }}
              >
                {label}
                <span className={styles.tabCount}>{count}</span>
              </button>
            ))}
          </div>
        </div>

        {!loading && rows.length === 0 ? (
          <div className={styles.empty}>{error && !kept ? 'Timesheets could not be loaded for this date.' : emptyLabel}</div>
        ) : (
          <div className={styles.table} role="table" aria-label="Shifts">
            <div className={styles.headRow} role="row">
              <button
                role="checkbox"
                aria-checked={allOn}
                aria-label="Select every worker shown"
                title="Select every worker shown"
                className={allOn ? `${styles.check} ${styles.checkOn}` : styles.check}
                onClick={() => setPicked(allOn ? [] : rows.map((r) => r.id))}
              >
                {allOn ? '✓' : ''}
              </button>
              {['Worker', 'Payable', 'Span', 'Teams and sessions', 'Breaks', 'State'].map((h) => (
                <span key={h} role="columnheader">
                  {h}
                </span>
              ))}
              <span />
            </div>
            {loading ? (
              <SkeletonRows rowClass={styles.row} cols={8} label="Loading shifts" />
            ) : (
              rows.map((sh) => (
                <ShiftRow
                  key={sh.id}
                  sh={sh}
                  on={picked.includes(sh.id)}
                  open={openId === sh.id}
                  teamFilter={teamFilter}
                  colourOf={colourOf}
                  onPick={() => toggle(sh.id)}
                  onOpen={() => setOpenId(sh.id)}
                />
              ))
            )}
          </div>
        )}

        <div className={styles.legend}>
          <span>
            <i className={styles.legendSwatch} style={{ background: '#fbe4dc' }} />
            Overlapping or incomplete capture. Totals stay provisional until corrected
          </span>
          <span>Payable time is recorded session time less any unpaid break not already covered by a gap</span>
        </div>
      </div>

      {openId && (
        <ShiftEditor
          key={openId}
          user={user}
          shiftId={openId}
          teams={pickableTeams}
          today={today}
          onClose={() => {
            setOpenId(null)
            setFlash('')
          }}
          onWritten={written}
        />
      )}
      {bulk && (
        <BulkModal
          user={user}
          op={bulk}
          shiftIds={picked}
          teams={pickableTeams}
          onClose={() => setBulk(null)}
          onApplied={(msg) => {
            setBulk(null)
            setPicked([])
            written(msg)
          }}
        />
      )}
      {schedOpen && (
        <BreakScheduleModal
          user={user}
          date={date}
          onClose={() => setSchedOpen(false)}
          onSaved={(msg) => {
            setSchedOpen(false)
            written(msg)
          }}
          onChanged={written}
        />
      )}
      {settingsOpen && (
        <TimesheetSettingsModal
          user={user}
          onClose={() => setSettingsOpen(false)}
          onSaved={(msg) => {
            setSettingsOpen(false)
            written(msg)
          }}
        />
      )}
      {adding && (
        <AddShiftModal
          user={user}
          date={date}
          people={people}
          teams={pickableTeams}
          onClose={() => setAdding(false)}
          onAdded={(msg, _id, d) => {
            setAdding(false)
            setFlash(msg)
            // Show the day the shift was added to; changing date reloads it.
            if (d !== date) goDate(d)
            else load()
          }}
          onOpenExisting={(id, d) => {
            setAdding(false)
            if (d !== date) goDate(d)
            setOpenId(id)
          }}
        />
      )}
      </PageBody>
    </div>
  )
}

function ShiftRow({
  sh,
  on,
  open,
  teamFilter,
  colourOf,
  onPick,
  onOpen,
}: {
  sh: Shift
  on: boolean
  open: boolean
  teamFilter: string | null
  colourOf: (id: string) => string
  onPick: () => void
  onOpen: () => void
}) {
  const overlap = sh.overlap_minutes || 0
  const cross = sh.cross_date_overlaps || []
  const incomplete = !!sh.incomplete || (sh.metrics?.open_session_ids?.length ?? 0) > 0
  const provisional = isProvisional(sh)
  const complete = hasComplete(sh)
  const span = spanOf(sh)
  const clockOn = span ? null : clockOnOf(sh)
  const manual = sh.paid_is_manual || sh.unpaid_is_manual
  const n = sh.session_count ?? (sh.sessions || []).length
  const tint = open ? styles.rowOpen : sh.exclude_from_wages ? styles.rowExcluded : overlap > 0 || cross.length ? styles.rowOverlap : incomplete ? styles.rowIncomplete : ''
  // The latest adjustment, compact on the row (h 1802-1803); the full list is in the panel.
  const lastAdj = [...(sh.adjustment_history || [])].reverse().find(isAdjustment)

  return (
    <div className={`${styles.row} ${tint}`} role="row" {...openableRow(onOpen)} data-testid="shift-row">
      <button
        role="checkbox"
        aria-checked={on}
        aria-label={`Select ${sh.worker_name}`}
        className={on ? `${styles.check} ${styles.checkOn}` : styles.check}
        onClick={(e) => {
          e.stopPropagation()
          onPick()
        }}
      >
        {on ? '✓' : ''}
      </button>
      <div role="cell">
        <div className={styles.worker}>{sh.worker_name}</div>
        {lastAdj && (
          <div className={styles.sub} data-testid="row-adjusted">
            Adjusted by {lastAdj.actor_email || lastAdj.actor} · {fmtStamp(lastAdj.timestamp)}
          </div>
        )}
      </div>
      <div role="cell">
        <div className={provisional ? `${styles.big} ${styles.provisional}` : styles.big}>{complete ? fmtDur(sh.net_payable_minutes) : '—'}</div>
        <div className={styles.sub}>{complete ? `${fmtDur(sh.recorded_minutes)} recorded` : 'No complete session'}</div>
      </div>
      <div role="cell">
        <div>
          {span ? `${fmtHm(span.s)} – ${fmtHm(span.e)}` : clockOn ? `${clockOn} – —` : '—'}
          {span && span.e >= 1440 && <span className={styles.plusDay}>+1 day</span>}
        </div>
        <div className={styles.sub}>{span ? `${fmtDur(sh.span_minutes ?? span.e - span.s)} span` : clockOn ? 'No clock-off' : ' '}</div>
      </div>
      <div role="cell">
        <div className={styles.chips}>
          {(sh.team_ids || []).map((id, i) => (
            <span key={id} className={teamFilter === id ? `${styles.teamChip} ${styles.teamChipOn}` : styles.teamChip}>
              <span className={styles.teamDot} style={{ background: colourOf(id) }} />
              {sh.team_names?.[i] || 'Team'}
            </span>
          ))}
        </div>
        <div className={styles.sub}>
          {n} session{n === 1 ? '' : 's'}
        </div>
      </div>
      <div role="cell" className={styles.breaks}>
        {sh.paid_minutes || 0}m paid · {sh.unpaid_minutes || 0}m unpaid{' '}
        <span className={manual ? `${styles.pill} ${styles.pillAccent}` : `${styles.pill} ${styles.pillGood}`}>{manual ? 'Manual' : 'Computed'}</span>
      </div>
      <div role="cell" className={styles.pills}>
        {overlap > 0 && <span className={`${styles.pill} ${styles.pillBad}`}>Overlap {fmtDur(overlap)}</span>}
        {cross.map((o) => (
          <span key={o.work_date} className={`${styles.pill} ${styles.pillBad}`} title="These times overlap the same worker’s shift on another work date">
            Overlaps {shortDateLabel(o.work_date)} {fmtDur(o.minutes)}
          </span>
        ))}
        {incomplete && <span className={`${styles.pill} ${styles.pillBad}`}>Missing clock-off</span>}
        {sh.exclude_from_wages && <span className={`${styles.pill} ${styles.pillMuted}`}>Excluded from wages</span>}
        {(sh.adjustment_history || []).some(isAdjustment) && <span className={`${styles.pill} ${styles.pillAccent}`}>Adjusted</span>}
      </div>
      <span className={styles.chev} style={{ transform: open ? 'rotate(180deg)' : undefined }}>
        ⌄
      </span>
    </div>
  )
}

function Calendar({
  month,
  picked,
  today,
  onMonth,
  onPick,
}: {
  month: string
  picked: string
  today: string
  onMonth: (m: string) => void
  onPick: (iso: string) => void
}) {
  const first = parseIsoLocal(month + '-01')
  const lead = (first.getDay() + 6) % 7 // Monday-first
  const days = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate()
  const shift = (n: number) => onMonth(isoLocal(new Date(first.getFullYear(), first.getMonth() + n, 1)).slice(0, 7))
  const cells: (string | null)[] = Array(lead).fill(null)
  for (let d = 1; d <= days; d++) cells.push(isoLocal(new Date(first.getFullYear(), first.getMonth(), d)))

  return (
    <div className={styles.calendar} role="dialog" aria-label="Pick a work date">
      <div className={styles.calHead}>
        <button className={styles.calNav} aria-label="Previous month" onClick={() => shift(-1)}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" aria-hidden="true"><path d="m15 18-6-6 6-6" /></svg>
        </button>
        <span className={styles.calTitle}>
          {MONS[first.getMonth()]} {first.getFullYear()}
        </span>
        <button className={styles.calNav} aria-label="Next month" onClick={() => shift(1)}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" aria-hidden="true"><path d="m9 18 6-6-6-6" /></svg>
        </button>
      </div>
      <div className={styles.calGrid}>
        {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((d, i) => (
          <span key={i} className={styles.calDow}>
            {d}
          </span>
        ))}
        {cells.map((iso, i) =>
          iso ? (
            <button
              key={iso}
              className={[styles.calDay, iso === today ? styles.calToday : '', iso === picked ? styles.calPicked : ''].join(' ')}
              style={iso > today && iso !== picked ? { color: 'var(--dp-muted2)' } : undefined}
              onClick={() => onPick(iso)}
            >
              {Number(iso.slice(8))}
            </button>
          ) : (
            <span key={'b' + i} />
          )
        )}
      </div>
      <div className={styles.calFoot}>
        <button className={styles.calTodayBtn} onClick={() => onPick(today)}>
          Today
        </button>
      </div>
    </div>
  )
}
